// components/insights/GameInsights.jsx
import React, { useRef, useReducer } from 'react';
import { useDiscovery } from '../../contexts/DiscoveryContext';
import { useAnalysis } from '../../contexts/AnalysisContext';
import ErrorBoundary from '../ErrorBoundary';
import CompetitiveAnalysisView from './competitive/CompetitiveAnalysisView';
import { LineChart, RefreshCw, AlertCircle, StopCircle } from 'lucide-react';
import { unwrapWishlistResponse } from '../../utils/wishlistUtils.js';

/**
 * IMPROVED COMPETITIVE ANALYSIS
 * 
 * This component analyzes games using real wishlist data from the Gamalytics API.
 * All metrics are calculated from actual competitor data, not placeholders.
 * 
 * Data Sources:
 * - Wishlist Insights API: Provides competitor games, geographic distribution, wishlist age
 * - Competitor Game Data: Revenue, players, wishlists, genres, release dates
 * 
 * Key Improvements:
 * 1. Real market share calculation based on revenue comparison
 * 2. Meaningful engagement metrics from wishlist age distribution
 * 3. Accurate geographic reach from country data
 * 4. Data-driven SWOT analysis with specific insights
 * 5. Transparent calculations with explanations
 */

// Loading state component
const LoadingState = ({ games, progress, onCancel }) => (
    <div className="p-8 text-center">
        <div className="bg-white rounded-lg shadow p-8">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-4">
                    <RefreshCw className="w-12 h-12 text-blue-500 animate-spin" />
                    <div>
                        <h3 className="text-lg font-medium text-gray-900">Analyzing Games</h3>
                        <p className="text-gray-500">
                            Processing {games.length} games...
                            {progress && <span> ({progress.current}/{progress.total})</span>}
                        </p>
                    </div>
                </div>
                <button
                    onClick={onCancel}
                    className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                >
                    <StopCircle className="w-4 h-4" />
                    Cancel
                </button>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: progress ? `${Math.min(100, (progress.current / progress.total) * 100)}%` : '100%' }}
                ></div>
            </div>
        </div>
    </div>
);

/**
 * CALCULATION: Filtered Market Analysis
 * Analyzes the competitive landscape within Game Discovery filtered results
 * 
 * Data Source: DiscoveryContext.discoveredGames (the games you filtered)
 * Denominator: Only the games in your filtered set
 * 
 * Why this matters: Shows your position within the specific market segment you're targeting
 * 
 * @param {Array} discoveredGames - Games from Game Discovery filters
 * @param {string} currentGameId - The game being analyzed (steamId)
 * @returns {Object} Filtered market analysis with clear scope
 */
const calculateFilteredMarketAnalysis = (discoveredGames, currentGameId) => {
    if (!discoveredGames || discoveredGames.length === 0) {
        return {
            available: false,
            reason: 'No filtered games available. Run Game Discovery first.',
            scope: { totalGames: 0 }
        };
    }

    // Find current game in the filtered set
    const currentGame = discoveredGames.find(g => g.steamId === currentGameId);
    const competitors = discoveredGames.filter(g => g.steamId !== currentGameId);

    if (!currentGame) {
        return {
            available: false,
            reason: 'Current game not found in filtered results.',
            scope: { totalGames: discoveredGames.length }
        };
    }

    // CALCULATION 1: Market Share (within filtered games only)
    const totalRevenue = discoveredGames.reduce((sum, g) => sum + (g.revenue || 0), 0);
    const thisGameRevenue = currentGame.revenue || 0;
    const marketSharePercent = totalRevenue > 0 ? (thisGameRevenue / totalRevenue) * 100 : 0;

    // CALCULATION 2: Rank within filtered set
    const sortedByRevenue = [...discoveredGames].sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
    const rank = sortedByRevenue.findIndex(g => g.steamId === currentGameId) + 1;

    // CALCULATION 3: Average metrics comparison
    const avgRevenue = totalRevenue / discoveredGames.length;
    const avgWishlists = discoveredGames.reduce((sum, g) => sum + (g.wishlists || 0), 0) / discoveredGames.length;
    const avgFollowers = discoveredGames.reduce((sum, g) => sum + (g.followers || 0), 0) / discoveredGames.length;

    return {
        available: true,
        scope: {
            totalGames: discoveredGames.length,
            description: `Analysis based on ${discoveredGames.length} games from your Game Discovery filters`
        },
        marketShare: {
            percentage: marketSharePercent,
            rank: rank,
            totalRank: discoveredGames.length,
            explanation: `This game represents ${marketSharePercent.toFixed(1)}% of revenue among the ${discoveredGames.length} filtered games (ranked #${rank}).`
        },
        comparison: {
            revenue: {
                value: thisGameRevenue,
                average: avgRevenue,
                percentOfAverage: avgRevenue > 0 ? (thisGameRevenue / avgRevenue) * 100 : 0
            },
            wishlists: {
                value: currentGame.wishlists || 0,
                average: avgWishlists,
                percentOfAverage: avgWishlists > 0 ? ((currentGame.wishlists || 0) / avgWishlists) * 100 : 0
            },
            followers: {
                value: currentGame.followers || 0,
                average: avgFollowers,
                percentOfAverage: avgFollowers > 0 ? ((currentGame.followers || 0) / avgFollowers) * 100 : 0
            }
        },
        competitors: competitors.map(g => ({
            steamId: g.steamId,
            name: g.name,
            revenue: g.revenue,
            wishlists: g.wishlists,
            followers: g.followers,
            genres: g.genres
        }))
    };
};

/**
 * Transform wishlist API data into competitive analysis
 * Uses REAL data - no placeholders or random values
 * NOW INCLUDES DUAL-SOURCE ANALYSIS: Filtered Market + Similar Games Network
 * 
 * @param {string} gameId - Steam ID of the game
 * @param {Object} wishlistData - Raw API response from /game/{id}/wishlist-insights
 * @param {string} gameName - Name of the game
 * @param {Object} gameData - Full game data including revenue, wishlists, etc.
 * @param {Array} discoveredGames - Filtered games from Game Discovery
 * @returns {Object} Structured competitive analysis with dual sources
 */
const transformWishlistToAnalysis = (gameId, wishlistData, gameName, gameData, discoveredGames) => {
    if (!wishlistData || typeof wishlistData !== 'object') {
        return null;
    }

    // Extract competitor data
    const competitors = wishlistData.alsoWishlisted || [];
    const competitorCount = competitors.length;

    // CALCULATION 1: Market Share
    // Based on this game's revenue vs total competitor revenue
    const marketShareData = calculateMarketShare(gameData, competitors);
    
    // CALCULATION 2: Audience Engagement  
    // Based on wishlist age - recent wishlists indicate active interest
    const audienceEngagementData = calculateAudienceEngagement(wishlistData.wishlistAge);
    
    // CALCULATION 3: Geographic Reach
    // Based on number of countries with wishlist presence
    const geographicReachData = calculateGeographicReach(wishlistData.wishlistsByCountry);
    
    // CALCULATION 4: Market Analysis
    const marketAnalysis = analyzeMarket(competitors, gameData);
    
    // CALCULATION 5: SWOT Analysis (data-driven)
    const swotAnalysis = generateSWOTAnalysis(
        wishlistData, 
        competitors, 
        gameData,
        marketShareData,
        geographicReachData
    );
    
    // CALCULATION 6: Market Position
    const marketPosition = determineMarketPosition(
        marketShareData,
        competitorCount,
        gameData
    );

    // CALCULATION 7: Filtered Market Analysis
    const filteredMarketAnalysis = calculateFilteredMarketAnalysis(discoveredGames, gameId);

    return {
        // Section 1: Your Filtered Market
        filteredMarket: filteredMarketAnalysis,

        // Section 2: Similar Games Network (Gamalytics)
        gamalyticsNetwork: {
            summary: {
                gameId,
                gameName,
                dataSource: 'Gamalytics API - Wishlist Insights',
                scope: {
                    description: `Analysis based on ${competitorCount} games that users also wishlisted`,
                    competitors: competitorCount
                },
                keyMetrics: {
                    marketShare: marketShareData.percentage,
                    marketShareExplanation: marketShareData.explanation,
                    audienceEngagement: audienceEngagementData.percentage,
                    engagementExplanation: audienceEngagementData.explanation,
                    geographicReach: geographicReachData.percentage,
                    reachExplanation: geographicReachData.explanation
                },
                overview: {
                    marketPosition,
                    competitorCount,
                    totalMarketRevenue: marketAnalysis.totalRevenue,
                    averageCompetitorRevenue: marketAnalysis.averageRevenue,
                    totalMarketRevenueLabel: 'Total Revenue of Similar Games Network'
                },
                strengths: swotAnalysis.strengths,
                weaknesses: swotAnalysis.weaknesses,
                opportunities: swotAnalysis.opportunities,
                threats: swotAnalysis.threats
            },
            competitors: {
                direct: {
                    competitors: competitors.map(c => ({
                        steamId: c.steamId,
                        name: c.name,
                        revenue: c.revenue,
                        wishlists: c.wishlists,
                        players: c.players,
                        genres: c.genres
                    })),
                    count: competitorCount
                }
            },
            market: {
                overview: {
                    size: {
                        totalRevenue: marketAnalysis.totalRevenue,
                        averageRevenue: marketAnalysis.averageRevenue,
                        medianRevenue: marketAnalysis.medianRevenue
                    },
                    stage: marketAnalysis.stage,
                    competitiveness: marketAnalysis.competitiveness
                }
            },
            dataQuality: {
                hasCompetitors: competitorCount > 0,
                hasGeographicData: Object.keys(wishlistData.wishlistsByCountry || {}).length > 0,
                hasWishlistAge: Object.keys(wishlistData.wishlistAge || {}).length > 0,
                dataSource: 'Gamalytics API - Wishlist Insights'
            }
        },

        metadata: {
            analyzedAt: new Date().toISOString(),
            dataSources: {
                filteredMarket: 'Game Discovery Filters',
                gamalyticsNetwork: 'Gamalytics Wishlist Insights API'
            }
        }
    };
};

/**
 * CALCULATION 1: Market Share
 * Compares this game's revenue to total competitor revenue
 * 
 * Why this matters: Shows your position in the market relative to direct competitors.
 * Lower market share = more room to grow, but also more competition.
 * 
 * @param {Object} gameData - This game's data (with revenue)
 * @param {Array} competitors - Competitor games from alsoWishlisted
 * @returns {Object} Market share percentage and explanation
 */
const calculateMarketShare = (gameData, competitors) => {
    if (!competitors || competitors.length === 0) {
        return {
            percentage: 0,
            explanation: 'No competitor data available. This could mean the game is in a niche market or data is not yet populated.',
            hasData: false
        };
    }

    // Sum up total market revenue (this game + competitors)
    const totalCompetitorRevenue = competitors.reduce((sum, c) => sum + (c.revenue || 0), 0);
    const thisGameRevenue = gameData?.revenue || 0;
    const totalMarketRevenue = totalCompetitorRevenue + thisGameRevenue;

    if (totalMarketRevenue === 0) {
        return {
            percentage: 0,
            explanation: 'Revenue data not available for this market segment.',
            hasData: false
        };
    }

    const marketSharePercent = (thisGameRevenue / totalMarketRevenue) * 100;

    // Generate contextual explanation
    let explanation = `Your game represents ${marketSharePercent.toFixed(1)}% of the total market revenue ($${(totalMarketRevenue / 1000000).toFixed(1)}M) `;
    
    if (marketSharePercent < 1) {
        explanation += 'among these competitors. This is a small share with significant growth opportunity.';
    } else if (marketSharePercent < 5) {
        explanation += 'among these competitors. You have room to grow but are establishing presence.';
    } else if (marketSharePercent < 15) {
        explanation += 'among these competitors. You are a notable player in this market.';
    } else {
        explanation += 'among these competitors. You are a major player in this market segment.';
    }

    return {
        percentage: marketSharePercent,
        explanation,
        hasData: true,
        totalMarketRevenue,
        thisGameRevenue
    };
};

/**
 * CALCULATION 2: Audience Engagement
 * Based on wishlist age distribution - shows how actively people are discovering the game
 * 
 * Why this matters: Recent wishlists indicate growing interest and market momentum.
 * Older wishlists may mean interest has plateaued.
 * 
 * @param {Object} wishlistAge - Age distribution from API (e.g., {"<1m": 0.15, "1-3m": 0.25})
 * @returns {Object} Engagement percentage and explanation
 */
const calculateAudienceEngagement = (wishlistAge) => {
    if (!wishlistAge || Object.keys(wishlistAge).length === 0) {
        return {
            percentage: 0,
            explanation: 'Wishlist age data not available.',
            hasData: false
        };
    }

    // Normalize age keys (handle escaped characters)
    const normalizedAge = {};
    Object.entries(wishlistAge).forEach(([key, value]) => {
        const normalizedKey = key.replace(/\\u003E/g, '>').replace(/\\u003C/g, '<');
        normalizedAge[normalizedKey] = value;
    });

    // Weight recent wishlists more heavily
    // <1m = 100% weight, 1-3m = 80%, 3-6m = 60%, 6-12m = 40%, >12m = 20%
    const recentShare = normalizedAge['<1m'] || 0;
    const oneToThree = normalizedAge['1-3m'] || 0;
    const threeToSix = normalizedAge['3-6m'] || 0;
    const sixToTwelve = normalizedAge['6-12m'] || 0;
    const older = normalizedAge['>12m'] || 0;

    // Weighted engagement score (0-100)
    const engagementScore = (
        recentShare * 100 +
        oneToThree * 80 +
        threeToSix * 60 +
        sixToTwelve * 40 +
        older * 20
    );

    // Generate contextual explanation
    let explanation = `${(recentShare * 100).toFixed(0)}% of wishlists were added in the last month. `;
    
    if (engagementScore > 75) {
        explanation += 'Strong recent momentum - your game is actively being discovered.';
    } else if (engagementScore > 50) {
        explanation += 'Moderate engagement with steady discovery rate.';
    } else if (engagementScore > 25) {
        explanation += 'Lower recent activity - consider marketing push to reignite interest.';
    } else {
        explanation += 'Wishlist growth has slowed - needs attention to regain momentum.';
    }

    return {
        percentage: engagementScore,
        explanation,
        hasData: true,
        breakdown: {
            recent: recentShare,
            oneToThree,
            threeToSix,
            sixToTwelve,
            older
        }
    };
};

/**
 * CALCULATION 3: Geographic Reach
 * Based on number of countries with wishlist presence
 * 
 * Why this matters: Geographic diversity reduces risk and indicates market potential.
 * More countries = broader appeal, but may need localization.
 * 
 * @param {Object} wishlistsByCountry - Country distribution (e.g., {"us": 0.30, "cn": 0.25})
 * @returns {Object} Geographic reach percentage and explanation
 */
const calculateGeographicReach = (wishlistsByCountry) => {
    if (!wishlistsByCountry || Object.keys(wishlistsByCountry).length === 0) {
        return {
            percentage: 0,
            explanation: 'Geographic distribution data not available.',
            hasData: false
        };
    }

    const countries = Object.entries(wishlistsByCountry);
    const countryCount = countries.length;

    // Identify major markets (top 3 by share)
    const topMarkets = countries
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([code, share]) => `${code.toUpperCase()} (${(share * 100).toFixed(1)}%)`);

    // Calculate diversity score (0-100)
    // 50+ countries = 100%, scale linearly
    const diversityScore = Math.min(100, (countryCount / 50) * 100);

    // Generate contextual explanation
    let explanation = `Active in ${countryCount} countries. Top markets: ${topMarkets.join(', ')}. `;
    
    if (diversityScore > 80) {
        explanation += 'Excellent global reach with diverse market presence.';
    } else if (diversityScore > 60) {
        explanation += 'Good geographic diversity with room for expansion.';
    } else if (diversityScore > 40) {
        explanation += 'Moderate reach - consider targeting additional regions.';
    } else if (diversityScore > 20) {
        explanation += 'Limited geographic presence - significant expansion opportunity.';
    } else {
        explanation += 'Very concentrated market - high risk if primary market declines.';
    }

    return {
        percentage: diversityScore,
        explanation,
        hasData: true,
        countryCount,
        topMarkets: countries.sort((a, b) => b[1] - a[1]).slice(0, 5)
    };
};

/**
 * CALCULATION 4: Market Analysis
 * Analyzes the overall market based on competitor data
 * 
 * @param {Array} competitors - Competitor games
 * @param {Object} gameData - This game's data
 * @returns {Object} Market analysis metrics
 */
const analyzeMarket = (competitors, gameData) => {
    if (!competitors || competitors.length === 0) {
        return {
            totalRevenue: gameData?.revenue || 0,
            averageRevenue: gameData?.revenue || 0,
            medianRevenue: gameData?.revenue || 0,
            stage: 'Unknown',
            competitiveness: 'Unknown'
        };
    }

    // Calculate market metrics
    const revenues = competitors.map(c => c.revenue || 0).filter(r => r > 0);
    const totalRevenue = revenues.reduce((sum, r) => sum + r, 0) + (gameData?.revenue || 0);
    const averageRevenue = revenues.length > 0 ? revenues.reduce((sum, r) => sum + r, 0) / revenues.length : 0;
    
    // Calculate median
    const sortedRevenues = [...revenues].sort((a, b) => a - b);
    const medianRevenue = sortedRevenues.length > 0 
        ? sortedRevenues[Math.floor(sortedRevenues.length / 2)]
        : 0;

    // Determine market stage
    let stage = 'Emerging';
    if (averageRevenue > 10000000) stage = 'Mature';
    else if (averageRevenue > 1000000) stage = 'Growing';

    // Determine competitiveness
    let competitiveness = 'Low';
    if (competitors.length > 20) competitiveness = 'Very High';
    else if (competitors.length > 10) competitiveness = 'High';
    else if (competitors.length > 5) competitiveness = 'Moderate';

    return {
        totalRevenue,
        averageRevenue,
        medianRevenue,
        stage,
        competitiveness,
        competitorCount: competitors.length
    };
};

/**
 * CALCULATION 5: SWOT Analysis (Data-Driven)
 * Generates Strengths, Weaknesses, Opportunities, and Threats based on real data patterns
 * 
 * @param {Object} wishlistData - Raw wishlist API data
 * @param {Array} competitors - Competitor games
 * @param {Object} gameData - This game's data
 * @param {Object} marketShareData - Calculated market share
 * @param {Object} geographicReachData - Calculated geographic reach
 * @returns {Object} SWOT analysis with specific insights
 */
const generateSWOTAnalysis = (wishlistData, competitors, gameData, marketShareData, geographicReachData) => {
    const strengths = [];
    const weaknesses = [];
    const opportunities = [];
    const threats = [];

    // Analyze geographic strengths/weaknesses
    if (geographicReachData.hasData && geographicReachData.topMarkets) {
        const topMarket = geographicReachData.topMarkets[0];
        const topMarketShare = topMarket[1];

        if (topMarketShare > 0.3) {
            strengths.push({
                strength: `Strong presence in ${topMarket[0].toUpperCase()} market (${(topMarketShare * 100).toFixed(1)}%)`,
                impact: 'high',
                reasoning: 'Dominant position in a major market provides stable revenue base'
            });
        }

        if (geographicReachData.countryCount < 10) {
            weaknesses.push({
                weakness: `Limited geographic reach (only ${geographicReachData.countryCount} countries)`,
                impact: 'medium',
                reasoning: 'High concentration risk - vulnerable to regional market changes'
            });

            // If limited reach, expansion is an opportunity
            opportunities.push({
                opportunity: `Geographic expansion to untapped markets`,
                impact: 'high',
                reasoning: `Currently only in ${geographicReachData.countryCount} countries - potential to 5x market size`,
                actionable: 'Consider localization for major markets: China, US, EU, Japan'
            });
        }
    }

    // Analyze competitive positioning
    if (competitors && competitors.length > 0) {
        const avgCompetitorRevenue = competitors.reduce((sum, c) => sum + (c.revenue || 0), 0) / competitors.length;
        const thisGameRevenue = gameData?.revenue || 0;

        if (thisGameRevenue > avgCompetitorRevenue * 1.5) {
            strengths.push({
                strength: 'Above-average revenue compared to direct competitors',
                impact: 'high',
                reasoning: `Your revenue ($${(thisGameRevenue / 1000000).toFixed(1)}M) exceeds average competitor ($${(avgCompetitorRevenue / 1000000).toFixed(1)}M)`
            });
        } else if (thisGameRevenue < avgCompetitorRevenue * 0.5) {
            weaknesses.push({
                weakness: 'Below-average revenue in this market segment',
                impact: 'medium',
                reasoning: 'Competitors are generating significantly more revenue - may indicate marketing or product gaps'
            });
        }

        // Competition intensity
        if (competitors.length > 15) {
            threats.push({
                threat: `High competition intensity (${competitors.length} direct competitors)`,
                impact: 'high',
                reasoning: 'Crowded market makes differentiation and visibility challenging'
            });
        } else if (competitors.length < 5) {
            opportunities.push({
                opportunity: 'Low competition environment',
                impact: 'medium',
                reasoning: `Only ${competitors.length} direct competitors - easier to establish market position`,
                actionable: 'Invest in marketing to capture market share before more competitors enter'
            });
        }
    }

    // Analyze wishlist momentum
    const wishlistAge = wishlistData.wishlistAge || {};
    const recentWishlists = wishlistAge['<1m'] || 0;
    
    if (recentWishlists > 0.2) {
        strengths.push({
            strength: 'Strong recent wishlist growth',
            impact: 'medium',
            reasoning: `${(recentWishlists * 100).toFixed(0)}% of wishlists added in last month - indicates growing interest`
        });
    } else if (recentWishlists < 0.05) {
        weaknesses.push({
            weakness: 'Slowing wishlist growth',
            impact: 'medium',
            reasoning: 'Low recent wishlist activity may indicate declining interest or visibility'
        });
        
        opportunities.push({
            opportunity: 'Revitalize marketing and visibility efforts',
            impact: 'high',
            reasoning: 'Current low visibility presents opportunity to regain momentum',
            actionable: 'Launch targeted marketing campaign, content updates, or sales promotion'
        });
    }

    // Default messages if no specific items found
    if (strengths.length === 0) {
        strengths.push({
            strength: 'Established market presence',
            impact: 'low',
            reasoning: 'Game has wishlist data and competitor analysis available'
        });
    }

    if (weaknesses.length === 0) {
        weaknesses.push({
            weakness: 'No significant weaknesses identified',
            impact: 'low',
            reasoning: 'Based on available data, no critical issues detected'
        });
    }

    if (opportunities.length === 0) {
        opportunities.push({
            opportunity: 'Market expansion through increased visibility',
            impact: 'medium',
            reasoning: 'Standard growth opportunity available in all markets',
            actionable: 'Invest in marketing, community building, or content updates'
        });
    }

    if (threats.length === 0) {
        threats.push({
            threat: 'No immediate threats identified',
            impact: 'low',
            reasoning: 'Based on available data, no critical threats detected'
        });
    }

    return { strengths, weaknesses, opportunities, threats };
};

/**
 * CALCULATION 6: Market Position
 * Determines competitive position based on market share, competition, and performance
 * 
 * @param {Object} marketShareData - Market share calculation results
 * @param {number} competitorCount - Number of direct competitors
 * @param {Object} gameData - This game's data
 * @returns {Object} Market position assessment
 */
const determineMarketPosition = (marketShareData, competitorCount, gameData) => {
    const marketShare = marketShareData.percentage || 0;
    
    // Determine position
    let position = 'Challenger';
    if (marketShare > 25) position = 'Leader';
    else if (marketShare > 15) position = 'Major Player';
    else if (marketShare > 5) position = 'Established';
    else if (marketShare > 1) position = 'Challenger';
    else position = 'Niche Player';

    // Determine trend based on wishlist data (simplified - could be enhanced with historical data)
    const trend = 'Stable'; // Would need historical data to determine actual trend

    // Determine strength
    let strength = 'Medium';
    if (marketShare > 20 && competitorCount < 10) strength = 'High';
    else if (marketShare < 2 && competitorCount > 20) strength = 'Low';

    return {
        position,
        trend,
        strength,
        explanation: `Classified as "${position}" based on ${marketShare.toFixed(1)}% market share among ${competitorCount} competitors`
    };
};

// Helper function to normalize wishlist age data
const normalizeWishlistAge = (wishlistAge) => {
    if (!wishlistAge || typeof wishlistAge !== 'object') {
        return {};
    }

    const normalized = {};
    Object.entries(wishlistAge).forEach(([key, value]) => {
        const normalizedKey = key.replace(/\\u003E/g, '>').replace(/\\u003C/g, '<');
        normalized[normalizedKey] = value;
    });

    return normalized;
};

const GameInsights = () => {
    // Get discovered games from context
    const { discoveredGames } = useDiscovery();

    // Get analysis context functions
    const { isAnalyzing, results, cancelAnalysis, startAnalysis } = useAnalysis();

    // Create refs to store state locally
    const analysisResultsRef = useRef(null);
    const progressRef = useRef({ current: 0, total: 0 });
    const isAnalyzingRef = useRef(false);

    // Force update function to trigger re-renders
    const [, forceUpdate] = useReducer(x => x + 1, 0);

    // Main analysis function
    const handleAnalysis = async () => {
        // Validation
        if (!discoveredGames?.length) {
            alert('Please find matching games in Discovery first');
            return;
        }

        // Set analyzing state
        isAnalyzingRef.current = true;
        progressRef.current = { current: 0, total: discoveredGames.length };
        forceUpdate();

        // Start analysis in context
        startAnalysis(discoveredGames);

        try {
            const analyzedGames = [];

            // Process each game
            for (let i = 0; i < discoveredGames.length; i++) {
                const game = discoveredGames[i];

                // Update progress
                progressRef.current = { current: i + 1, total: discoveredGames.length };
                forceUpdate();

                try {
                    // Fetch wishlist data
                    const response = await fetch(`/analytics/api/game/${game.steamId}/wishlist-insights`);

                    if (!response.ok) {
                        analyzedGames.push({
                            ...game,
                            competitiveAnalysis: null,
                            analysisError: 'Failed to fetch wishlist data'
                        });
                        continue;
                    }

                    const rawPayload = await response.json();
                    const wishlistData = unwrapWishlistResponse(rawPayload);

                    if (!wishlistData) {
                        analyzedGames.push({
                            ...game,
                            competitiveAnalysis: null,
                            analysisError: 'Invalid wishlist data'
                        });
                        continue;
                    }

                    // Normalize wishlist age data if present
                    if (wishlistData.wishlistAge) {
                        wishlistData.wishlistAge = normalizeWishlistAge(wishlistData.wishlistAge);
                    }

                    // Transform wishlist data into competitive analysis using DUAL-SOURCE logic
                    const competitiveAnalysis = transformWishlistToAnalysis(
                        game.steamId,
                        wishlistData,
                        game.name,
                        game, // Pass full game data for revenue comparisons
                        discoveredGames // Pass filtered games for filtered market analysis
                    );

                    // Add the game with analysis results
                    analyzedGames.push({
                        ...game,
                        competitiveAnalysis: competitiveAnalysis || null,
                        analysisError: competitiveAnalysis ? null : 'Analysis unavailable'
                    });
                } catch (error) {
                    console.error(`Error processing wishlist data for ${game.name}:`, error);
                    analyzedGames.push({
                        ...game,
                        competitiveAnalysis: null,
                        analysisError: error.message
                    });
                }
            }

            // Prepare final results
            const finalResults = {
                summary: {
                    totalGames: analyzedGames.length,
                    analyzedGames: analyzedGames.filter(g => g.competitiveAnalysis).length,
                    errorCount: analyzedGames.filter(g => !g.competitiveAnalysis).length
                },
                gameAnalyses: analyzedGames,
                competitiveAnalysis: analyzedGames.find(g => g.competitiveAnalysis)?.competitiveAnalysis || null,
            };

            // Store results in ref and update UI
            analysisResultsRef.current = finalResults;
            isAnalyzingRef.current = false;
            forceUpdate();

        } catch (error) {
            console.error("Analysis failed:", error);
            isAnalyzingRef.current = false;
            forceUpdate();
        }
    };

    // Handler for canceling analysis
    const handleCancelAnalysis = () => {
        cancelAnalysis();
        isAnalyzingRef.current = false;
        forceUpdate();
    };

    // If no games selected, show empty state
    if (!discoveredGames?.length) {
        return (
            <div className="p-8 text-center">
                <LineChart className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Games Selected</h3>
                <p className="text-gray-500">Use Game Discovery to find and select games for analysis</p>
            </div>
        );
    }

    // Show loading state while analyzing
    if (isAnalyzingRef.current) {
        return (
            <LoadingState
                games={discoveredGames}
                progress={progressRef.current}
                onCancel={handleCancelAnalysis}
            />
        );
    }

    // Show results if available
    if (analysisResultsRef.current || results) {
        const displayResults = analysisResultsRef.current || results;
        const hasAnalysis = displayResults?.competitiveAnalysis;
        const failedGames = displayResults?.gameAnalyses?.filter(g => !g.competitiveAnalysis) || [];

        return (
            <ErrorBoundary>
                <div className="p-8 space-y-6">
                    {/* Show full competitive analysis directly */}
                    {hasAnalysis ? (
                        <CompetitiveAnalysisView 
                            analysis={displayResults.competitiveAnalysis}
                            onNewAnalysis={handleAnalysis}
                            summary={displayResults.summary}
                        />
                    ) : (
                        <div className="bg-white rounded-lg shadow p-6 text-center">
                            <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
                            <h3 className="text-lg font-medium mb-2">No Analysis Available</h3>
                            <p className="text-gray-600 mb-4">
                                Unable to generate competitive analysis from the selected games.
                            </p>
                            <button
                                onClick={handleAnalysis}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Retry Analysis
                            </button>
                        </div>
                    )}

                    {/* Show error messages for failed games */}
                    {failedGames.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="text-sm font-medium text-gray-700">
                                Analysis Issues ({failedGames.length} games)
                            </h4>
                            {failedGames.map(game => (
                                <div key={game.steamId} className="bg-gray-100 text-gray-600 p-3 rounded-lg flex items-center gap-2 text-sm">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    <span>{game.name}: {game.analysisError || 'Analysis unavailable'}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </ErrorBoundary>
        );
    }

    // Default state - show analyze button
    return (
        <div className="p-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-blue-900 mb-2">About This Analysis</h4>
                <p className="text-blue-800 text-sm">
                    This tool analyzes your selected games using real wishlist data from the Gamalytics API.
                    All metrics are calculated from actual competitor data - no placeholders or estimates.
                </p>
                <p className="text-blue-700 text-sm mt-2">
                    <strong>Data Sources:</strong> Competitor revenue, wishlist age, geographic distribution, market trends
                </p>
            </div>
            <button
                onClick={handleAnalysis}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
                <LineChart className="w-4 h-4" />
                Analyze {discoveredGames.length} Games
            </button>
        </div>
    );
};

export default GameInsights;
